import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { money, date, statusColor } from '../lib/format.js';
import { Modal } from '../components/Modal.jsx';

export default function DocumentView() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['document', id], queryFn: () => api(`/api/documents/${id}`) });
  const [showEmail, setShowEmail] = useState(false);
  const [showCn, setShowCn] = useState(false);

  const send = useMutation({
    mutationFn: () => api(`/api/documents/${id}/send`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['document', id] }),
  });
  const cancel = useMutation({
    mutationFn: () => api(`/api/documents/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['document', id] }),
  });
  const convert = useMutation({
    mutationFn: () => api(`/api/documents/${id}/convert-to-invoice`, { method: 'POST' }),
    onSuccess: r => nav(`/documents/${r.document.id}`),
  });

  if (!data) return <div className="card">Loading…</div>;
  const d = data.document;
  const balance = Number(d.total) - Number(d.paidTotal);

  return (
    <>
      <div className="page-header">
        <div>
          <h1 style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {d.fullNumber} <span className="badge" style={{ background: statusColor(d.status), color: 'white' }}>{d.status}</span>
          </h1>
          <div className="muted">{d.type} · {d.client?.name}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {d.status === 'DRAFT' && (
            <>
              <Link className="btn btn-secondary" to={`/documents/${d.id}/edit`}>Edit</Link>
              <button className="btn btn-secondary" onClick={() => window.confirm('Cancel this draft?') && cancel.mutate()}>Cancel</button>
              <button className="btn btn-primary" onClick={() => send.mutate()} disabled={send.isPending}>
                {send.isPending ? <span className="spinner" /> : 'Send / Issue'}
              </button>
            </>
          )}
          {d.status !== 'DRAFT' && (
            <>
              <a className="btn btn-secondary" href={`/api/documents/${d.id}/pdf`} target="_blank" rel="noreferrer">PDF</a>
              <button className="btn btn-secondary" onClick={() => setShowEmail(true)}>Email</button>
              {d.type === 'INVOICE' && !['CANCELLED', 'VOIDED'].includes(d.status) && (
                <button className="btn btn-secondary" onClick={() => setShowCn(true)}>Credit note</button>
              )}
              {['QUOTE', 'PROFORMA'].includes(d.type) && (
                <button className="btn btn-primary" onClick={() => convert.mutate()}>Convert to Invoice</button>
              )}
            </>
          )}
        </div>
      </div>

      {send.error && <div className="error-banner">{send.error.data?.error === 'oversell' ? `Not enough stock for ${send.error.data.extra?.sku}` : send.error.message}</div>}

      <div className="card">
        <div className="row">
          <div>
            <h3>Bill to</h3>
            <div><strong>{d.client.name}</strong></div>
            {d.client.legalName && <div className="muted">{d.client.legalName}</div>}
            {d.client.nuis && <div className="muted">NUIS: {d.client.nuis}</div>}
            <div className="muted">{d.client.address} {d.client.city} {d.client.country}</div>
          </div>
          <div>
            <h3>Details</h3>
            <div>Issue: {date(d.issueDate)}</div>
            {d.dueDate && <div>Due: {date(d.dueDate)}</div>}
            <div>Currency: {d.currencyCode}</div>
            {d.publicToken && <div>Public: <a href={`/p/${d.publicToken}`} target="_blank" rel="noreferrer">/p/{d.publicToken.slice(0, 8)}…</a></div>}
          </div>
        </div>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>SKU</th><th>Item</th>
            <th style={{ textAlign: 'right' }}>Qty</th><th>Unit</th>
            <th style={{ textAlign: 'right' }}>Price</th>
            <th style={{ textAlign: 'right' }}>Disc %</th>
            <th style={{ textAlign: 'right' }}>VAT %</th>
            <th style={{ textAlign: 'right' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {d.items.map(it => (
            <tr key={it.id}>
              <td>{it.sku}</td>
              <td><strong>{it.name}</strong>{it.description && <div className="muted">{it.description}</div>}</td>
              <td style={{ textAlign: 'right' }}>{Number(it.qty).toFixed(2)}</td>
              <td>{it.unit}</td>
              <td style={{ textAlign: 'right' }}>{money(it.unitPrice, d.currencyCode)}</td>
              <td style={{ textAlign: 'right' }}>{Number(it.discountPct).toFixed(2)}</td>
              <td style={{ textAlign: 'right' }}>{Number(it.vatPct).toFixed(2)}</td>
              <td style={{ textAlign: 'right' }}>{money(it.lineTotal, d.currencyCode)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="card" style={{ maxWidth: 400, marginLeft: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotal</span><strong>{money(Number(d.subtotal) - Number(d.discountTotal), d.currencyCode)}</strong></div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>VAT</span><strong>{money(d.vatTotal, d.currencyCode)}</strong></div>
        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid var(--navy-900)', marginTop: 8, paddingTop: 8, fontSize: 16, color: 'var(--navy-900)' }}>
          <span>Total</span><strong>{money(d.total, d.currencyCode)}</strong>
        </div>
        {Number(d.paidTotal) > 0 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}><span>Paid</span><strong>−{money(d.paidTotal, d.currencyCode)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: balance > 0 ? 'var(--danger)' : 'var(--success)' }}>
              <span>Balance</span><strong>{money(balance, d.currencyCode)}</strong>
            </div>
          </>
        )}
      </div>

      {d.allocations?.length > 0 && (
        <div className="card">
          <h3>Payments</h3>
          <table className="table">
            <thead><tr><th>Date</th><th>Reference</th><th>Method</th><th style={{ textAlign: 'right' }}>Allocated</th></tr></thead>
            <tbody>
              {d.allocations.map(a => (
                <tr key={a.id}>
                  <td>{date(a.payment.receivedAt)}</td><td>{a.payment.reference || '—'}</td>
                  <td>{a.payment.method}</td>
                  <td style={{ textAlign: 'right' }}>{money(a.amount, d.currencyCode)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showEmail && <EmailModal doc={d} onClose={() => setShowEmail(false)} />}
      {showCn && <CreditNoteModal doc={d} onClose={() => setShowCn(false)} onDone={cn => nav(`/documents/${cn.id}`)} />}
    </>
  );
}

function EmailModal({ doc, onClose }) {
  const [to, setTo] = useState(doc.client.email || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async e => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await api(`/api/email/documents/${doc.id}/send-email`, { method: 'POST', body: { to } });
      onClose();
    } catch (e) { setErr(e.data?.error || e.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Email ${doc.fullNumber}`} onClose={onClose}>
      <form onSubmit={submit}>
        {err && <div className="error-banner">{err}</div>}
        <div className="field"><label>To</label><input type="email" required value={to} onChange={e => setTo(e.target.value)} /></div>
        <div className="muted">PDF is attached automatically. Uses the default `invoice.send` template in the client's language.</div>
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? <span className="spinner" /> : 'Send'}</button>
        </div>
      </form>
    </Modal>
  );
}

function CreditNoteModal({ doc, onClose, onDone }) {
  const [items, setItems] = useState(doc.items.map(it => ({
    articleId: it.articleId, sku: it.sku, name: it.name, description: it.description,
    qty: Number(it.qty), unit: it.unit, unitPrice: Number(it.unitPrice),
    discountPct: Number(it.discountPct), vatRateId: it.vatRateId, vatPct: Number(it.vatPct),
    include: true,
  })));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api(`/api/documents/${doc.id}/credit-note`, { method: 'POST', body: {
        reason,
        items: items.filter(i => i.include).map(i => ({
          articleId: i.articleId, sku: i.sku, name: i.name, description: i.description,
          qty: i.qty, unit: i.unit, unitPrice: i.unitPrice,
          discountPct: i.discountPct, vatRateId: i.vatRateId, vatPct: i.vatPct,
        })),
      }});
      onDone(r.document);
    } finally { setBusy(false); }
  };
  return (
    <Modal title={`Credit note for ${doc.fullNumber}`} onClose={onClose} wide>
      <form onSubmit={submit}>
        <div className="field"><label>Reason</label><input required value={reason} onChange={e => setReason(e.target.value)} /></div>
        <table className="table">
          <thead><tr><th></th><th>Item</th><th>Qty</th><th>Price</th><th>VAT %</th></tr></thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i}>
                <td><input type="checkbox" checked={it.include} onChange={e => setItems(x => { const c = [...x]; c[i] = { ...c[i], include: e.target.checked }; return c; })} /></td>
                <td>{it.name}</td>
                <td><input type="number" step="0.01" style={{ width: 80 }} value={it.qty} onChange={e => setItems(x => { const c = [...x]; c[i] = { ...c[i], qty: Number(e.target.value) }; return c; })} /></td>
                <td>{money(it.unitPrice, doc.currencyCode)}</td>
                <td>{it.vatPct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? <span className="spinner" /> : 'Issue credit note'}</button>
        </div>
      </form>
    </Modal>
  );
}
